<template>
  <div class="index" />
  <!-- Mobile modal -->
  <overlay
    v-if="isMobile && !isMonogram && foundRecommendations"
    :namespace="namespace"
    :template="false"
    block-class="quick-add-overlay"
    direction="up"
    type="drawer"
    :teleport-content="true"
  >
    <!-- quickAddMobileProduct now comes from this component -->
    <template #body="{ isActive }">
      <quick-add-mobile
        v-cloak
        v-if="getComponentVisible('quick-add-mobile')"
        :is-active="isActive"
        :namespace="namespace"
        :preselected-options="preselectedOptions"
        :product="quickAddMobileProduct"
        :product-card-color="allColors && allColors[selectedColorIndex]"
        :wishlist="wishlist"
        @update-selected-variant="
          variant => $emit('update-selected-variant', variant)
        "
      />
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Index
 * -----------------------------------------------------------------------------
 * Used to set index states and global event listeners.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import breakpoints from '~/config/breakpoints'
import { queryProductById } from '~/helpers/query'
import * as productHelpers from '~/helpers/product'
import { debounce, throttle } from '~/helpers/general'
import Cookies from 'js-cookie'

export default {
  name: 'Index',

  components: {
    QuickAddMobile: defineAsyncComponent({
      loader: () => import(
        /* webpackPrefetch: true */
        '~async/quick-add-mobile/quick-add-mobile'
        ),
    }),
  },

  data() {
    return {
      global: true,
      screen: {
        breakpoint: 'xs',
        mobile: true,
        height: 0,
        width: 0,
      },
      scroll: {
        direction: 'none',
        header: 'open',
        top: true,
        x: 0,
        y: 0,
      },
      quickAddMobileProduct: {},
      selectedColorIndex: 0,
      selectedOptions: [],
      foundRecommendations: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      activeOverlays: (state) => state.overlays.active,
      settings: (state) => state.index.settings,
      isMobile: (state) => state.index.screen.mobile,
      cart: (state) => state.cart.response,
    }),

    quickAddMobileProduct(){

    /**
     * Set the initial options
     */
      this.selectedOptions = this.quickAddMobileProduct.options.map(
        option => {
          if (option.name.match(/color/gi)) {
            this.colorIndex = option.index
          }
          return { name: option.name, value: '' }
        },
      )

      return this.quickAddMobileProduct
    },

    selectedColorIndex(){
      return this.selectedColorIndex
    },

    isMonogram() {
      return this.quickAddMobileProduct?.tags?.filter(tag => tag.match(/monogram/gi)).length
    },

    /**
    * Compute all colors from product options.
    * @returns {Array}
    */
    allColors() {
      if (
        !Array.isArray(this.quickAddMobileProduct.options) ||
        this.quickAddMobileProduct.options[0].name.toLowerCase() !== 'color'
      ) {
        return null
      }

      const colors = this.quickAddMobileProduct.variants.reduce((agg, item) => {
        if(item?.selectedOptions.length){
          agg.push(item.selectedOptions[0].value.split(' / ')[0])
        }
        return agg
      }, []).map(color => color.trim())

      /**
       * Move preselected color to the beginning of the swatch colors.
       */
      if (this.preselectedColor && colors) {
        const colorIndex = colors.map(color =>
          color.toLowerCase(),
        ).indexOf(this.preselectedColor.toLowerCase())

        if (colorIndex > -1) {
          const color = colors.splice(colorIndex, 1)
          colors.unshift(color[0])
        }
      }

      return [...new Set(colors)]
    },

  },

  beforeMount(){
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const schoolCode = urlParams.get('sc')

    if(schoolCode){
      const inTwelveHours = new Date(new Date().getTime() + 12 * 60 * 60 * 1000)
      Cookies.set('sc', schoolCode, { expires: inTwelveHours })
    }
  },

  mounted() {
    if(!window.location.href.includes('/pages')
    || window.location.href.includes('/pages/style-appointment')
    || window.location.href.includes('/pages/visit-us')){
      const ssElement = document.getElementById('ss-404-container')
      if(ssElement){
        ssElement.remove()
      }
    }
    if(!window.location.pathname.includes('collections')){
      this.handleSearchspringRecommendations()
    }
    this.rehydrateSite()
    this.getPreviousUrl()

    this.handleResize()
    this.handleScroll(true)

    this.setEventListeners()
    this.setEventBusListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      rehydrateSite: 'rehydrateSite',
      setPreviousUrl: 'setPreviousUrl',
      setScreen: 'setScreen',
      setScroll: 'setScroll',
      setStylesheet: 'setStylesheet',
    }),

    /**
     * Get previous URL from sessionStorage.
     * - State doesn't update in time if set in the `beforeunload` event.
     */
    getPreviousUrl() {
      const previousUrl = sessionStorage.getItem('previousUrl')

      if (previousUrl) {
        this.setPreviousUrl(previousUrl)
      }
    },

    active(variantId) {
      const { wishlist_variant_ids } = this.cart.attributes

      if (!wishlist_variant_ids || !variantId) {
        return false
      }

      const variants = JSON.parse(JSON.stringify(wishlist_variant_ids))
      return variants.indexOf(variantId) > -1
    },

    /**
     * Update the wishlist modal with the selected variant / product and open
     * the modal.
     */
     openWishlistModal(variantId, productId, button) {
      if (variantId === '') {
        return
      }

      this.updateModal(variantId, productId, button)

      if (!this.active(variantId)) {
        setTimeout(()=>{
          this.openOverlay({
            component: 'gift-reggie',
            ignoreDismissed: true,
            namespace: 'giftReggie',
          })
        }, 600)
      }

    },

    updateModal(variantId, productId, button) {
      cnvs.EventBus.emit('giftReggie:update', {
        variantId,
        productId,
        action: this.active(variantId) ? 'remove' : 'add',
        button: button,
      })
    },

    handleSearchspringRecommendations(){
      const elem = document.getElementById('vue-element')

      let isSSRecommendationSectionUp = false

      const interval = setInterval(()=>{
        if(isSSRecommendationSectionUp){
          console.log('SS Recommendations is Up')
          this.foundRecommendations = true
          let ssRecommendationsObserver = new MutationObserver(mutationRecords => {
            this.handleButtonSync()
          })

          const ssRecommendationsElem = document.getElementsByClassName('ss__recommendation')[0]

          ssRecommendationsObserver.observe(ssRecommendationsElem, {
            childList: true,
            subtree: true,
            characterDataOldValue: true,
            attributes: true,
            attributeFilter: ['data-variant-id'],
          })

          // If there are two SS Recommendations, these lines will pick it up and observe it
          // This is needed for the Swatches to work properly
          if(document.getElementsByClassName('ss__recommendation')[1]){
            const ssRecommendationsElemSecond = document.getElementsByClassName('ss__recommendation')[1]
            console.log('Second SS Recommendation is UP!')
            this.handleButtonSync()
            ssRecommendationsObserver.observe(ssRecommendationsElemSecond, {
              childList: true,
              subtree: true,
              characterDataOldValue: true,
              attributes: true,
              attributeFilter: ['data-variant-id'],
            })
          }

          clearInterval(interval)
        }
      },1000)

      let observer = new MutationObserver(mutationRecords => {
        mutationRecords.forEach(mutationRecord => {
          if(mutationRecord.target.children[0]?.className?.toString().includes("ss__recommendation")){
            isSSRecommendationSectionUp = true
            this.handleButtonSync()
            observer.disconnect()
          }
        })
      })


      observer.observe(elem, {
        childList: true,
        subtree: true,
        characterDataOldValue: true,
        attributes: true,
        attributeFilter: ['data-variant-id'],
      })

    },

    handleButtonSync(){
      const addToCartButtons = []
      const singleAddToCartButtons = document.getElementsByClassName("quick-add__button quick-add__add-cart")
      const sizeAddToCartButtons = document.getElementsByClassName("quick-add__size-button")
      const mobileAddToCartButtons = document.querySelectorAll("button.product-card__quick-add.quick-add")

      addToCartButtons.push(...Array.from(singleAddToCartButtons),
                  ...Array.from(sizeAddToCartButtons),
                  ...Array.from(mobileAddToCartButtons))

      addToCartButtons.forEach(button => {

        const variantId = Number(button.getAttribute('data-variant-id'))
        const productId = Number(button.getAttribute('data-uid'));

        (async () => {
          const product = await queryProductById(productId)

          if(product?.tags.includes('preorder2024') || product?.tags.includes('purple-dot-live-waitlist')){

            button.onclick = (e) => {
              e.preventDefault()
              e.stopPropagation()

              window.location = product?.url || window.location.href
            }

          }else{

            button.onclick = (e) => {
              e.preventDefault()
              e.stopPropagation()
              console.log('I clicked on Add to Cart button in index.vue!')
              if(!this.isMobile){

                (async () => {
                  const product = await queryProductById(productId)
                  this.handleAddToCart(variantId, product, button)
                  if(window.location.pathname.includes('cart')){
                    $('html, body').animate({ scrollTop: 0 }, 'fast')
                  }
                })().catch(e => {
                    console.log(e)
                })

              }else{

                (async () => {
                  if(window.location.pathname.includes('cart')){
                    const product = await queryProductById(productId)
                    this.handleAddToCart(variantId, product, button)
                    $('html, body').animate({ scrollTop: 0 }, 'fast')
                  } else {
                    const product = await queryProductById(productId)
                    this.quickAddMobileProduct = product
                    this.handleOpenMobileQuickAdd(product)
                  }
                })().catch(e => {
                    console.log(e)
                })

              }
            }

          }
        })().catch(e => {
            console.log(e)
        })
      })


      const addToWishlistButtons = Array.from(document.getElementsByClassName("product-card__wishlist wishlist-button product-card__action"))

      addToWishlistButtons.forEach(button => {
        const variantId = Number(button.getAttribute('data-variant-id'))
        const productId = Number(button.getAttribute('data-uid'));

        (async () => {
          const product = await queryProductById(productId)

          if(product?.tags.includes('preorder2024') || product?.tags.includes('purple-dot-live-waitlist')){
            button.classList.add('remove-wishlist-button')
          }
        })().catch(e => {
            console.log(e)
        })

        if(this.active(variantId)){
          button.classList.add('wishlist-active')
        }

        button.onclick = (e) => {
          e.preventDefault()
          e.stopPropagation()
          this.openWishlistModal(variantId, productId, button)
        }
      })

      const swatchButtons = Array.from(document.querySelectorAll("button.color-swatch.boost-listings__product-card__product-card-color-swatch"))

      swatchButtons.forEach(swatchButton => {
        const color = swatchButton.getAttribute('data-color')

        swatchButton.onclick = (e) => {
          e.preventDefault()
          e.stopPropagation()
          const productId = swatchButton.parentNode.parentNode.parentNode.parentNode.nextSibling.getAttribute('data-uid');

          (async () => {
            const product = await queryProductById(productId)
            this.quickAddMobileProduct = product
            const index = product.options[0].values.indexOf(color)
            this.selectedColorIndex = index
          })().catch(e => {
              console.log(e)
          })

        }
      })
    },

    /**
     * Add to cart and open the cart drawer.
     */
     async handleAddToCart(variantId, product, button) {
      if (this.isAddingToCart) {
        return
      }
      const buttonOriginalInnerHtml = button.innerHTML
      try {
        const variant = product.variants.find(variant => variant.id === variantId)

        const properties = productHelpers.getLineItemProperties(product, variant)

        this.isAddingToCart = true

        button.innerHTML = this.$icon('misc.loading')

        await this.addItem([
          {
            id: variantId,
            quantity: 1,
            properties,
            variant: variant,
            product: product,
          },
        ])

        let type = 'filter'

        if (window.cnvs.page.type === 'search') {
          type = 'search'
        }

        this.type = type

        this.analyticsEvent = {
          pid: product.id.toString(),
          u: 'add_to_cart',
        }

        this.isAddingToCart = false

        button.innerHTML = buttonOriginalInnerHtml

        if(!window.location.pathname.includes('cart')){
          this.openOverlay({
            namespace: 'cartDrawer',
            ignoreDismissed: true,
            component: 'cart-drawer',
          })
        }

        this.$gtmTrack('AddtoCart', this.$variable('analytics.quickAdd'))
      }
      catch (error) {
        this.isAddingToCart = false
        button.innerHTML = buttonOriginalInnerHtml
        console.error('[QuickAddDesktop]', error)
      }
    },

    /**
     * Open mobile quick add and track event
     */
     handleOpenMobileQuickAdd(product) {
        this.openOverlay({
          component: 'quick-add-mobile',
          ignoreDismissed: true,
          namespace: '',
        })

        this.$gtmTrack('open_quick_view', 'product_tile_quick_view', {
          product: {
            title: product.title,
            handle: product.handle,
          },
        })
      },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      this.setStylesheetListeners()

      /**
       * Add default listeners.
       */
      window.visualViewport.addEventListener(
        'resize',
        debounce(() => this.handleResize(), this.settings.throttle),
      )

      window.addEventListener(
        'scroll',
        throttle(() => this.handleScroll(), this.settings.throttle),
      )

      /**
       * Set previous URL just before changing page.
       */
      window.addEventListener('beforeunload', () => {
        sessionStorage.setItem('previousUrl', location.pathname)
      })
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {

      /**
       * Overlay listeners act as fallbacks for non-Vue components.
       */
      cnvs.EventBus.listen('overlay:open', (overlay) => {
        this.openOverlay(overlay)
      })

      cnvs.EventBus.listen('overlay:close', (overlay) => {
        this.closeOverlay(overlay)
      })
    },

    /**
     * Listen for stylesheets loading to trigger events after they've loaded.
     */
    setStylesheetListeners() {
      const stylesheets = [...document.querySelectorAll('[rel="stylesheet"]')]

      stylesheets.forEach((element) => {
        const media = element.getAttribute('media')
        let stylesheet = element.getAttribute('href')

        if (!stylesheet) {
          return
        }

        if (stylesheet.includes('cdn.shopify')) {
          stylesheet = stylesheet
            .split('/')
            .reverse()[0]
            .split('?')[0]
        }

        /**
         * If it's already loaded then add to state.
         */
        if (media === 'all') {
          this.setStylesheet(stylesheet)
          return
        }

        /**
         * Otherwise listen for load.
         */
        element.addEventListener('load', () => {
          element.setAttribute('media', 'all')
          this.setStylesheet(stylesheet)
        })
      })
    },

    /**
     * Handle resize.
     */
    handleResize() {
      this.screen = {
        height: window.visualViewport.height,
        width: window.innerWidth,
      }

      /**
       * Set CSS variable.
       */
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${this.screen.height}px`,
      )

      if (window.matchMedia(`(min-width: ${breakpoints.xl})`).matches) {
        this.screen.breakpoint = 'xl'
      } else if (window.matchMedia(`(min-width: ${breakpoints.l})`).matches) {
        this.screen.breakpoint = 'l'
      } else if (window.matchMedia(`(min-width: ${breakpoints.m})`).matches) {
        this.screen.breakpoint = 'm'
      } else if (window.matchMedia(`(min-width: ${breakpoints.s})`).matches) {
        this.screen.breakpoint = 's'
      } else {
        this.screen.breakpoint = 'xs'
      }

      this.screen.mobile = Boolean(
        this.screen.breakpoint !== 'l' &&
        this.screen.breakpoint !== 'xl',
      )

      cnvs.EventBus.emit('cnvs:resize', (this.screen))
      this.setScreen(this.screen)
    },

    /**
     * Handle scroll.
     * - Don't update state when overlay opens.
     * @param {Boolean} [start] - If setting on initial load.
     */
    handleScroll(start = false) {
      if (this.activeOverlays.length) {
        return
      }

      /**
       * Set object on start.
       */
      if (start) {
        this.scroll = {
          direction: 'none',
          header: 'open',
          top: window.scrollY <= this.settings.start,
          x: window.scrollX,
          y: window.scrollY,
        }

        return
      }

      /**
       * If scroll difference is less than threshold don't update.
       */
      const difference = window.scrollY - this.scroll.y

      if (Math.abs(difference) < this.settings.threshold) {
        return
      }

      this.scroll = {
        direction: difference > 0 ? 'down' : 'up',
        top: window.scrollY <= this.settings.start,
        x: window.scrollX,
        y: window.scrollY,
      }

      if (this.scroll.direction === 'up' && !this.scroll.top) {
        this.scroll.header = 'minimised'
      } else if (window.scrollY > 0) {
        this.scroll.header = 'collapsed'
      } else {
        this.scroll.header = 'open'
      }

      cnvs.EventBus.emit('cnvs:scroll', (this.scroll))
      this.setScroll(this.scroll)
    },
  },
}
</script>

<style lang="scss">
  @import './index';
</style>
